import { For, Show, createMemo } from 'solid-js';
import Text from '../Text';
import { useTranslation } from '../../hooks/useTranslation';
import PaintEditor from './PaintEditor';
import {
    MINECRAFT_FONT_FAMILY,
    isPixelFont,
    pixelStrokeBlocks,
    snapFontSize,
    type Layer,
    type Paint,
} from '../../mapart/document';
import { DITHER_METHODS } from '../../mapart/quantize';
import { FOLLOW_BUILD } from '../../mapart/types';
import styles from './LayerPanel.module.css';

export const TEXT_FONTS = [
    { key: 'MAPART_FONT_SANS', family: "'Inter', sans-serif" },
    { key: 'MAPART_FONT_MONO', family: "'JetBrains Mono', monospace" },
    { key: 'MAPART_FONT_MINECRAFT', family: MINECRAFT_FONT_FAMILY },
];

// The weights the app actually loads. Inter covers all of them, JetBrains Mono stops at Semibold and
// falls back above that.
const TEXT_WEIGHTS = [
    { key: 'MAPART_WEIGHT_LIGHT', value: 300 },
    { key: 'MAPART_WEIGHT_REGULAR', value: 400 },
    { key: 'MAPART_WEIGHT_MEDIUM', value: 500 },
    { key: 'MAPART_WEIGHT_SEMIBOLD', value: 600 },
    { key: 'MAPART_WEIGHT_BOLD', value: 700 },
    { key: 'MAPART_WEIGHT_EXTRABOLD', value: 800 },
];

// One face, so bold is the redraw the game itself does. There is no lighter version of a bitmap: any
// thinning deletes the one pixel details the glyphs are made of.
const PIXEL_WEIGHTS = TEXT_WEIGHTS.filter((weight) => [400, 700].includes(weight.value));

export const weightsFor = (fontFamily: string) => (isPixelFont(fontFamily) ? PIXEL_WEIGHTS : TEXT_WEIGHTS);

// No shared list/form component fits: the rows carry per-kind property editors, so this reuses the
// global btn/label patterns instead of FilterRail (filtering) or Modal (overlay content).
interface Props {
    layers: Layer[];
    /** Last entry is the primary, whose values these editors show. */
    selectedIds: string[];
    /** Where the selection sits as a whole, which is what the X and Z fields move. */
    box: { x: number; y: number } | null;
    onSelect: (id: string, additive: boolean) => void;
    onToggleVisible: (id: string) => void;
    onReorder: (id: string, direction: -1 | 1) => void;
    onDelete: (id: string) => void;
    /** Applies across the selection; the page decides what a mixed selection may share. */
    onPatch: (patch: Partial<Layer>) => void;
    onMove: (axis: 'x' | 'y', value: number) => void;
    onAlign: (mode: 'start' | 'centre' | 'end', axis: 'x' | 'y') => void;
    onDistribute: (axis: 'x' | 'y') => void;
    /** Both match the other layers to the primary, the one whose values these editors show. */
    onMatchPosition: () => void;
    onMatchSize: () => void;
    /** Selected layers and groups counted as blocks, which is what aligning actually moves. */
    unitCount: number;
    onGroup: () => void;
    onUngroup: () => void;
    onCommit: () => void;
}

const ALIGN_BUTTONS: {
    mode: 'start' | 'centre' | 'end';
    axis: 'x' | 'y';
    icon: string;
    key: string;
}[] = [
    { mode: 'start', axis: 'x', icon: 'fa-solid fa-align-left', key: 'MAPART_ALIGN_LEFT' },
    { mode: 'centre', axis: 'x', icon: 'fa-solid fa-align-center', key: 'MAPART_ALIGN_CENTRE_X' },
    { mode: 'end', axis: 'x', icon: 'fa-solid fa-align-right', key: 'MAPART_ALIGN_RIGHT' },
    { mode: 'start', axis: 'y', icon: 'fa-solid fa-angles-up', key: 'MAPART_ALIGN_TOP' },
    { mode: 'centre', axis: 'y', icon: 'fa-solid fa-grip-lines', key: 'MAPART_ALIGN_CENTRE_Y' },
    { mode: 'end', axis: 'y', icon: 'fa-solid fa-angles-down', key: 'MAPART_ALIGN_BOTTOM' },
];

const round2 = (value: number) => Math.round(value * 100) / 100;

const KIND_LABELS: Record<Layer['kind'], string> = {
    image: 'MAPART_KIND_IMAGE',
    text: 'MAPART_KIND_TEXT',
    shape: 'MAPART_KIND_SHAPE',
};

const layerIcon = (layer: Layer) =>
    layer.kind === 'text' ? 'fa-solid fa-font' : layer.kind === 'shape' ? 'fa-solid fa-shapes' : 'fa-solid fa-image';

export default function LayerPanel(props: Props) {
    const { t } = useTranslation();
    const primaryId = createMemo(() => props.selectedIds[props.selectedIds.length - 1] ?? null);
    const selected = createMemo(() => props.layers.find((l) => l.id === primaryId()) ?? null);
    const selectedCount = createMemo(() => props.selectedIds.length);
    const mixedKinds = createMemo(() => {
        const kinds = new Set(
            props.layers.filter((l) => props.selectedIds.includes(l.id)).map((l) => l.kind),
        );
        return kinds.size > 1;
    });
    const anyGrouped = createMemo(() =>
        props.layers.some((layer) => props.selectedIds.includes(layer.id) && layer.groupId !== undefined),
    );
    const patch = (values: Partial<Layer>) => props.onPatch(values);

    return (
        <div class="v" style={{ gap: 'var(--spacing-md)' }}>
            <span class={styles.groupLabel}><Text val="MAPART_LAYERS" /></span>

            <Show when={props.layers.length > 0} fallback={
                <p class={styles.hint}>
                    <i class="fa-solid fa-layer-group" /> <Text val="MAPART_LAYERS_EMPTY" />
                </p>
            }>
                <div class={styles.list}>
                    <For each={[...props.layers].reverse()}>{(layer) => (
                        <div
                            class={styles.row}
                            classList={{
                                [styles.rowActive]: props.selectedIds.includes(layer.id),
                                [styles.rowPrimary]: layer.id === primaryId() && selectedCount() > 1,
                                [styles.rowGrouped]: layer.groupId !== undefined,
                            }}
                            onClick={(e) => props.onSelect(layer.id, e.shiftKey || e.ctrlKey || e.metaKey)}
                        >
                            <button
                                type="button"
                                class="btn btn-secondary btn-sm btn-icon"
                                title={t(layer.visible ? 'MAPART_LAYER_HIDE' : 'MAPART_LAYER_SHOW')}
                                onClick={(e) => { e.stopPropagation(); props.onToggleVisible(layer.id); }}
                            >
                                <i class={layer.visible ? 'fa-solid fa-eye' : 'fa-solid fa-eye-slash'} />
                            </button>
                            <span class={styles.name}>
                                <i class={layerIcon(layer)} />
                                {layer.name}
                                <Show when={layer.groupId !== undefined}>
                                    <i class={`fa-solid fa-object-group ${styles.groupMark}`} title={t('MAPART_GROUPED')} />
                                </Show>
                            </span>
                            <div class={styles.rowActions}>
                                <button
                                    type="button"
                                    class="btn btn-secondary btn-sm btn-icon"
                                    title={t('MAPART_LAYER_UP')}
                                    onClick={(e) => { e.stopPropagation(); props.onReorder(layer.id, 1); }}
                                >
                                    <i class="fa-solid fa-arrow-up" />
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-secondary btn-sm btn-icon"
                                    title={t('MAPART_LAYER_DOWN')}
                                    onClick={(e) => { e.stopPropagation(); props.onReorder(layer.id, -1); }}
                                >
                                    <i class="fa-solid fa-arrow-down" />
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-danger btn-sm btn-icon"
                                    title={t('DELETE')}
                                    onClick={(e) => { e.stopPropagation(); props.onDelete(layer.id); }}
                                >
                                    <i class="fa-solid fa-trash" />
                                </button>
                            </div>
                        </div>
                    )}</For>
                </div>
            </Show>

            <Show when={selectedCount() > 1}>
                <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span class={styles.fieldLabel}><Text val="MAPART_ARRANGE" /></span>
                    <div class={styles.arrangeRow}>
                        <For each={ALIGN_BUTTONS}>{(button) => (
                            <button
                                type="button"
                                class="btn btn-secondary btn-sm btn-icon"
                                title={t(button.key)}
                                disabled={props.unitCount < 2}
                                onClick={() => props.onAlign(button.mode, button.axis)}
                            >
                                <i class={button.icon} />
                            </button>
                        )}</For>
                    </div>
                    <div class={styles.arrangeRow}>
                        <button
                            type="button"
                            class="btn btn-secondary btn-sm"
                            title={t('MAPART_MATCH_POSITION_HINT')}
                            disabled={props.unitCount < 2}
                            onClick={props.onMatchPosition}
                        >
                            <i class="fa-solid fa-crosshairs" /> <Text val="MAPART_MATCH_POSITION" />
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary btn-sm"
                            title={t('MAPART_MATCH_SIZE_HINT')}
                            disabled={props.unitCount < 2}
                            onClick={props.onMatchSize}
                        >
                            <i class="fa-solid fa-maximize" /> <Text val="MAPART_MATCH_SIZE" />
                        </button>
                    </div>
                    <div class={styles.arrangeRow}>
                        <button
                            type="button"
                            class="btn btn-secondary btn-sm"
                            title={t('MAPART_DISTRIBUTE_X_HINT')}
                            disabled={props.unitCount < 3}
                            onClick={() => props.onDistribute('x')}
                        >
                            <i class="fa-solid fa-arrows-left-right" /> <Text val="MAPART_DISTRIBUTE_X" />
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary btn-sm"
                            title={t('MAPART_DISTRIBUTE_Y_HINT')}
                            disabled={props.unitCount < 3}
                            onClick={() => props.onDistribute('y')}
                        >
                            <i class="fa-solid fa-arrows-up-down" /> <Text val="MAPART_DISTRIBUTE_Y" />
                        </button>
                    </div>
                    <div class={styles.arrangeRow}>
                        <button
                            type="button"
                            class="btn btn-secondary btn-sm"
                            title={t('MAPART_GROUP_HINT')}
                            disabled={props.unitCount < 2}
                            onClick={props.onGroup}
                        >
                            <i class="fa-solid fa-object-group" /> <Text val="MAPART_GROUP" />
                        </button>
                        <button
                            type="button"
                            class="btn btn-secondary btn-sm"
                            title={t('MAPART_UNGROUP_HINT')}
                            disabled={!anyGrouped()}
                            onClick={props.onUngroup}
                        >
                            <i class="fa-solid fa-object-ungroup" /> <Text val="MAPART_UNGROUP" />
                        </button>
                    </div>
                </div>

                <p class={styles.hint}>
                    <i class="fa-solid fa-object-group" />{' '}
                    {mixedKinds()
                        ? t('MAPART_SELECTION_MIXED', selectedCount(), t(KIND_LABELS[selected()!.kind]))
                        : t('MAPART_SELECTION_COUNT', selectedCount())}
                </p>
            </Show>

            <Show when={selected()} keyed>
                {(layer) => (
                    <div class={styles.props}>
                        <Show when={layer.kind === 'image'}>
                            <label class={styles.checkRow} title={t('MAPART_IMAGE_PIXEL_PERFECT_HINT')}>
                                <input
                                    type="checkbox"
                                    checked={(layer as Extract<Layer, { kind: 'image' }>).pixelPerfect}
                                    onChange={(e) => { patch({ pixelPerfect: e.currentTarget.checked } as Partial<Layer>); props.onCommit(); }}
                                />
                                <span class={styles.fieldLabel}><Text val="MAPART_IMAGE_PIXEL_PERFECT" /></span>
                            </label>

                            <Show
                                when={(layer as Extract<Layer, { kind: 'image' }>).pixelPerfect}
                                fallback={
                                    <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                        <span class={styles.fieldLabel}><Text val="MAPART_PREP_RESAMPLE" /></span>
                                        <select
                                            value={(layer as Extract<Layer, { kind: 'image' }>).resample}
                                            onChange={(e) => { patch({ resample: e.currentTarget.value } as Partial<Layer>); props.onCommit(); }}
                                        >
                                            <option value="area">{t('MAPART_PREP_AREA')}</option>
                                            <option value="smooth">{t('MAPART_PREP_SMOOTH')}</option>
                                            <option value="pixel">{t('MAPART_PREP_PIXEL')}</option>
                                        </select>
                                    </label>
                                }
                            >
                                <span class={styles.hint}>
                                    {layer.scale >= 1
                                        ? t('MAPART_IMAGE_PIXEL_HINT', Math.round(layer.scale), Math.round(layer.scale))
                                        : t('MAPART_IMAGE_PIXEL_DOWN_HINT', Math.round(1 / layer.scale))}
                                </span>
                            </Show>

                            <For each={[
                                { key: 'brightness', label: 'MAPART_PREP_BRIGHTNESS', min: 0, max: 2, step: 0.01 },
                                { key: 'contrast', label: 'MAPART_PREP_CONTRAST', min: 0, max: 2, step: 0.01 },
                                { key: 'saturation', label: 'MAPART_PREP_SATURATION', min: 0, max: 3, step: 0.01 },
                                { key: 'blur', label: 'MAPART_PREP_BLUR', min: 0, max: 8, step: 0.1 },
                            ] as const}>{(field) => (
                                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span class={styles.fieldLabel}>
                                        {t(field.label)}{' '}
                                        <span class={styles.value}>
                                            {round2((layer as unknown as Record<string, number>)[field.key])}
                                        </span>
                                    </span>
                                    <div class={styles.sliderRow}>
                                        <input
                                            type="range"
                                            min={field.min}
                                            max={field.max}
                                            step={field.step}
                                            value={(layer as unknown as Record<string, number>)[field.key]}
                                            onInput={(e) => patch({ [field.key]: Number(e.currentTarget.value) } as Partial<Layer>)}
                                            onChange={() => props.onCommit()}
                                        />
                                        <input
                                            type="number"
                                            min={field.min}
                                            max={field.max}
                                            step={field.step}
                                            value={round2((layer as unknown as Record<string, number>)[field.key])}
                                            onInput={(e) => {
                                                const next = Number(e.currentTarget.value);
                                                if (Number.isFinite(next)) patch({ [field.key]: next } as Partial<Layer>);
                                            }}
                                            onChange={() => props.onCommit()}
                                        />
                                    </div>
                                </label>
                            )}</For>

                            <button
                                type="button"
                                class="btn btn-secondary btn-sm"
                                onClick={() => {
                                    patch({ brightness: 1, contrast: 1, saturation: 1, blur: 0 } as Partial<Layer>);
                                    props.onCommit();
                                }}
                            >
                                <i class="fa-solid fa-rotate-left" /> <Text val="MAPART_PREP_RESET" />
                            </button>
                        </Show>

                        <Show when={layer.kind === 'shape'}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_SHAPE_KIND" /></span>
                                <select
                                    value={(layer as Extract<Layer, { kind: 'shape' }>).shape}
                                    onChange={(e) => { patch({ shape: e.currentTarget.value } as Partial<Layer>); props.onCommit(); }}
                                >
                                    <option value="rect">{t('MAPART_SHAPE_RECT')}</option>
                                    <option value="ellipse">{t('MAPART_SHAPE_ELLIPSE')}</option>
                                    <option value="triangle">{t('MAPART_SHAPE_TRIANGLE')}</option>
                                    <option value="line">{t('MAPART_SHAPE_LINE')}</option>
                                </select>
                            </label>

                            <div class={styles.pair}>
                                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span class={styles.fieldLabel}><Text val="MAPART_SHAPE_WIDTH" /></span>
                                    <input
                                        type="number"
                                        min="1"
                                        value={(layer as Extract<Layer, { kind: 'shape' }>).width}
                                        onInput={(e) => patch({ width: Math.max(1, Number(e.currentTarget.value)) } as Partial<Layer>)}
                                        onChange={() => props.onCommit()}
                                    />
                                </label>
                                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span class={styles.fieldLabel}><Text val="MAPART_SHAPE_HEIGHT" /></span>
                                    <input
                                        type="number"
                                        min="1"
                                        value={(layer as Extract<Layer, { kind: 'shape' }>).height}
                                        onInput={(e) => patch({ height: Math.max(1, Number(e.currentTarget.value)) } as Partial<Layer>)}
                                        onChange={() => props.onCommit()}
                                    />
                                </label>
                            </div>

                            <Show when={(layer as Extract<Layer, { kind: 'shape' }>).shape === 'rect'}>
                                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span class={styles.fieldLabel}><Text val="MAPART_SHAPE_RADIUS" /></span>
                                    <input
                                        type="number"
                                        min="0"
                                        value={(layer as Extract<Layer, { kind: 'shape' }>).radius}
                                        onInput={(e) => patch({ radius: Math.max(0, Number(e.currentTarget.value)) } as Partial<Layer>)}
                                        onChange={() => props.onCommit()}
                                    />
                                </label>
                            </Show>
                        </Show>

                        <Show when={layer.kind === 'text' || layer.kind === 'shape'}>
                            <PaintEditor
                                label={t('MAPART_PAINT_FILL')}
                                paint={(layer as Extract<Layer, { kind: 'shape' }>).fill}
                                onChange={(fill: Paint) => patch({ fill } as Partial<Layer>)}
                                onCommit={props.onCommit}
                            />
                            <PaintEditor
                                label={t('MAPART_PAINT_STROKE')}
                                paint={(layer as Extract<Layer, { kind: 'shape' }>).stroke}
                                onChange={(stroke: Paint) => patch({ stroke } as Partial<Layer>)}
                                onCommit={props.onCommit}
                            />
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}>
                                    <Text val="MAPART_PAINT_STROKE_WIDTH" />{' '}
                                    <span class={styles.value}>{(layer as Extract<Layer, { kind: 'shape' }>).strokeWidth}</span>
                                </span>
                                <div class={styles.sliderRow}>
                                    <input
                                        type="range"
                                        min="0"
                                        max="16"
                                        step="0.5"
                                        value={(layer as Extract<Layer, { kind: 'shape' }>).strokeWidth}
                                        onInput={(e) => patch({ strokeWidth: Number(e.currentTarget.value) } as Partial<Layer>)}
                                        onChange={() => props.onCommit()}
                                    />
                                    <input
                                        type="number"
                                        min="0"
                                        max="64"
                                        step="0.5"
                                        value={(layer as Extract<Layer, { kind: 'shape' }>).strokeWidth}
                                        onInput={(e) => patch({ strokeWidth: Math.max(0, Number(e.currentTarget.value)) } as Partial<Layer>)}
                                        onChange={() => props.onCommit()}
                                    />
                                </div>
                            </label>
                        </Show>

                        <Show when={layer.kind === 'text'}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_TEXT_CONTENT" /></span>
                                <input
                                    type="text"
                                    value={(layer as Extract<Layer, { kind: 'text' }>).text}
                                    onInput={(e) => patch({ text: e.currentTarget.value } as Partial<Layer>)}
                                    onChange={() => props.onCommit()}
                                />
                            </label>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_TEXT_SIZE" /></span>
                                <input
                                    type="number"
                                    min="4"
                                    max="256"
                                    value={(layer as Extract<Layer, { kind: 'text' }>).fontSize}
                                    onInput={(e) => patch({ fontSize: Number(e.currentTarget.value) } as Partial<Layer>)}
                                    onChange={(e) => {
                                        // While snapping, the field has to show the size that is drawn, or typing a
                                        // value between two steps looks like nothing happened.
                                        const text = layer as Extract<Layer, { kind: 'text' }>;
                                        if (isPixelFont(text.fontFamily) && text.pixelSnap) {
                                            const snapped = snapFontSize(Number(e.currentTarget.value));
                                            if (snapped !== text.fontSize) patch({ fontSize: snapped } as Partial<Layer>);
                                        }
                                        props.onCommit();
                                    }}
                                />
                            </label>

                            <Show when={isPixelFont((layer as Extract<Layer, { kind: 'text' }>).fontFamily)}>
                                <label class={styles.checkRow}>
                                    <input
                                        type="checkbox"
                                        checked={(layer as Extract<Layer, { kind: 'text' }>).pixelSnap}
                                        onChange={(e) => { patch({ pixelSnap: e.currentTarget.checked } as Partial<Layer>); props.onCommit(); }}
                                    />
                                    <span class={styles.fieldLabel}><Text val="MAPART_TEXT_PIXEL_SNAP" /></span>
                                </label>

                                <label class={styles.checkRow} title={t('MAPART_TEXT_MONOSPACE_HINT')}>
                                    <input
                                        type="checkbox"
                                        checked={(layer as Extract<Layer, { kind: 'text' }>).monospace}
                                        onChange={(e) => { patch({ monospace: e.currentTarget.checked } as Partial<Layer>); props.onCommit(); }}
                                    />
                                    <span class={styles.fieldLabel}><Text val="MAPART_TEXT_MONOSPACE" /></span>
                                </label>
                                <span class={styles.hint}>
                                    {(layer as Extract<Layer, { kind: 'text' }>).pixelSnap
                                        ? t('MAPART_TEXT_PIXEL_HINT', pixelStrokeBlocks(layer as Extract<Layer, { kind: 'text' }>))
                                        : t('MAPART_TEXT_PIXEL_FREE_HINT')}
                                </span>
                            </Show>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_TEXT_FONT" /></span>
                                <select
                                    value={(layer as Extract<Layer, { kind: 'text' }>).fontFamily}
                                    onChange={(e) => { patch({ fontFamily: e.currentTarget.value } as Partial<Layer>); props.onCommit(); }}
                                >
                                    <For each={TEXT_FONTS}>{(font) => (
                                        <option value={font.family}>{t(font.key)}</option>
                                    )}</For>
                                </select>
                            </label>

                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_TEXT_WEIGHT" /></span>
                                <select
                                    value={String((layer as Extract<Layer, { kind: 'text' }>).fontWeight)}
                                    onChange={(e) => { patch({ fontWeight: Number(e.currentTarget.value) } as Partial<Layer>); props.onCommit(); }}
                                >
                                    <For each={weightsFor((layer as Extract<Layer, { kind: 'text' }>).fontFamily)}>{(weight) => (
                                        <option value={String(weight.value)}>{t(weight.key)}</option>
                                    )}</For>
                                </select>
                            </label>

                        </Show>

                        <Show when={layer.kind === 'text' || layer.kind === 'shape'}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}><Text val="MAPART_TEXT_EDGES" /></span>
                                <select
                                    value={(layer as Extract<Layer, { kind: 'shape' }>).edges}
                                    onChange={(e) => { patch({ edges: e.currentTarget.value } as Partial<Layer>); props.onCommit(); }}
                                >
                                    <option value="hard">{t('MAPART_TEXT_EDGES_HARD')}</option>
                                    <option value="smooth">{t('MAPART_TEXT_EDGES_SMOOTH')}</option>
                                </select>
                                <span class={styles.hint}>
                                    <Text val={(layer as Extract<Layer, { kind: 'shape' }>).edges === 'hard'
                                        ? 'MAPART_TEXT_EDGES_HARD_HINT'
                                        : 'MAPART_TEXT_EDGES_SMOOTH_HINT'} />
                                </span>
                            </label>

                            <Show when={(layer as Extract<Layer, { kind: 'shape' }>).edges === 'hard'}>
                                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                    <span class={styles.fieldLabel}>
                                        <Text val="MAPART_TEXT_EDGE_CUTOFF" />{' '}
                                        <span class={styles.value}>
                                            {Math.round((layer as Extract<Layer, { kind: 'shape' }>).edgeCutoff * 100)}%
                                        </span>
                                    </span>
                                    <input
                                        type="range"
                                        min="0.1"
                                        max="0.9"
                                        step="0.05"
                                        value={(layer as Extract<Layer, { kind: 'shape' }>).edgeCutoff}
                                        onInput={(e) => patch({ edgeCutoff: Number(e.currentTarget.value) } as Partial<Layer>)}
                                        onChange={() => props.onCommit()}
                                    />
                                </label>
                            </Show>
                        </Show>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="MAPART_LAYER_STAIRCASE" /></span>
                            <select
                                value={layer.staircase}
                                onChange={(e) => { patch({ staircase: e.currentTarget.value as Layer['staircase'] }); props.onCommit(); }}
                            >
                                <option value={FOLLOW_BUILD}>{t('MAPART_LAYER_FOLLOW_BUILD')}</option>
                                <option value="off">{t('MAPART_STAIRCASE_OFF')}</option>
                                <option value="classic">{t('MAPART_STAIRCASE_CLASSIC')}</option>
                            </select>
                            <span class={styles.hint}><Text val="MAPART_LAYER_STAIRCASE_HINT" /></span>
                        </label>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="MAPART_LAYER_DITHER" /></span>
                            <select
                                value={layer.dither}
                                onChange={(e) => { patch({ dither: e.currentTarget.value }); props.onCommit(); }}
                            >
                                <option value={FOLLOW_BUILD}>{t('MAPART_LAYER_FOLLOW_BUILD')}</option>
                                <For each={DITHER_METHODS}>{(method) => (
                                    <option value={method.key}>{method.label ?? t('MAPART_DITHER_NONE')}</option>
                                )}</For>
                            </select>
                            <span class={styles.hint}><Text val="MAPART_LAYER_DITHER_HINT" /></span>
                        </label>

                        <Show when={layer.dither !== FOLLOW_BUILD && layer.dither !== 'none'}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}>
                                    <Text val="MAPART_DITHER_STRENGTH" />{' '}
                                    <span class={styles.value}>{Math.round(layer.ditherStrength * 100)}%</span>
                                </span>
                                <div class={styles.sliderRow}>
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        value={layer.ditherStrength}
                                        onInput={(e) => patch({ ditherStrength: Number(e.currentTarget.value) })}
                                        onChange={() => props.onCommit()}
                                    />
                                    <input
                                        type="number"
                                        min="0"
                                        max="1"
                                        step="0.05"
                                        value={round2(layer.ditherStrength)}
                                        onInput={(e) => {
                                            const next = Number(e.currentTarget.value);
                                            if (Number.isFinite(next)) patch({ ditherStrength: Math.max(0, Math.min(1, next)) });
                                        }}
                                        onChange={() => props.onCommit()}
                                    />
                                </div>
                            </label>
                        </Show>

                        <div class={styles.pair}>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}>X</span>
                                <input
                                    type="number"
                                    value={Math.round(props.box?.x ?? layer.x)}
                                    onInput={(e) => props.onMove('x', Number(e.currentTarget.value))}
                                    onChange={() => props.onCommit()}
                                />
                            </label>
                            <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                                <span class={styles.fieldLabel}>Z</span>
                                <input
                                    type="number"
                                    value={Math.round(props.box?.y ?? layer.y)}
                                    onInput={(e) => props.onMove('y', Number(e.currentTarget.value))}
                                    onChange={() => props.onCommit()}
                                />
                            </label>
                        </div>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}><Text val="MAPART_LAYER_SCALE" /></span>
                            <div class={styles.sliderRow}>
                                <input
                                    type="range"
                                    min="0.05"
                                    max="8"
                                    step="0.01"
                                    value={layer.scale}
                                    onInput={(e) => patch({ scale: Number(e.currentTarget.value) })}
                                    onChange={() => props.onCommit()}
                                />
                                <input
                                    type="number"
                                    min="0.01"
                                    max="64"
                                    step="0.01"
                                    value={round2(layer.scale)}
                                    onInput={(e) => {
                                        const next = Number(e.currentTarget.value);
                                        if (Number.isFinite(next) && next > 0) patch({ scale: next });
                                    }}
                                    onChange={() => props.onCommit()}
                                />
                            </div>
                        </label>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}>
                                <Text val="MAPART_LAYER_ROTATION" /> <span class={styles.value}>{Math.round(layer.rotation)}°</span>
                            </span>
                            <div class={styles.sliderRow}>
                                <input
                                    type="range"
                                    min="-180"
                                    max="180"
                                    step="1"
                                    value={layer.rotation}
                                    onInput={(e) => patch({ rotation: Number(e.currentTarget.value) })}
                                    onChange={() => props.onCommit()}
                                />
                                <input
                                    type="number"
                                    min="-180"
                                    max="180"
                                    step="1"
                                    value={Math.round(layer.rotation)}
                                    onInput={(e) => {
                                        const next = Number(e.currentTarget.value);
                                        if (Number.isFinite(next)) patch({ rotation: next });
                                    }}
                                    onChange={() => props.onCommit()}
                                />
                            </div>
                        </label>

                        <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                            <span class={styles.fieldLabel}>
                                <Text val="MAPART_LAYER_OPACITY" /> <span class={styles.value}>{Math.round(layer.opacity * 100)}%</span>
                            </span>
                            <div class={styles.sliderRow}>
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={layer.opacity}
                                    onInput={(e) => patch({ opacity: Number(e.currentTarget.value) })}
                                    onChange={() => props.onCommit()}
                                />
                                <input
                                    type="number"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={round2(layer.opacity)}
                                    onInput={(e) => {
                                        const next = Number(e.currentTarget.value);
                                        if (Number.isFinite(next)) patch({ opacity: next });
                                    }}
                                    onChange={() => props.onCommit()}
                                />
                            </div>
                        </label>
                    </div>
                )}
            </Show>
        </div>
    );
}
